import { useMemo, useState } from 'react';

type Props<T extends Record<string, unknown>> = {
  entities: readonly T[];
  filterKey: keyof T;
  defaultEntity: T;
  onEntityChange: (entity: T) => void;
};

type Result<T extends Record<string, unknown>> = {
  isOpened: boolean;
  selectedEntity?: T;
  searchText: string;
  filteredEntities: readonly T[];
  handleOpenDropdown: VoidFunction;
  handleCloseDropdown: VoidFunction;
  handleSearchText: (searchText: string) => void;
  handleSelectEntity: (entity: T) => void;
};

export const useAccountDropdown = <T extends Record<string, unknown>>({
  entities,
  filterKey,
  defaultEntity,
  onEntityChange,
}: Props<T>): Result<T> => {
  const [isOpened, setIsOpened] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<T | undefined>(defaultEntity);
  const [searchText, setSearchText] = useState('');

  const searchWords = useMemo(() => searchText.toLowerCase().split(' '), [searchText]);

  const filteredEntities = useMemo(
    () =>
      entities.filter(
        (entity) =>
          typeof entity[filterKey] === 'string' &&
          searchWords.every((word) => {
            return (entity[filterKey] as string).toLowerCase().includes(word);
          })
      ),
    [entities, searchWords]
  );

  const handleOpenDropdown = () => setIsOpened(true);
  const handleCloseDropdown = () => setIsOpened(false);

  const handleSelectEntity = (entity: T) => {
    setSelectedEntity(entity);
    setIsOpened(false);
    onEntityChange(entity);
  };

  return {
    isOpened,
    handleOpenDropdown,
    handleCloseDropdown,
    selectedEntity,
    searchText,
    handleSearchText: setSearchText,
    filteredEntities,
    handleSelectEntity,
  };
};
